/**
 * @file 複数の資料の索引 (語 → ページ) をまとめて取得する hook。
 *
 * @what: 資料 id の並びを受け、id ごとに索引を読んで Map にする。取れなかった資料は
 *   空の索引として扱う (対応づけが引けないだけで、読む操作は止めない)。
 * @why: 資料どうしの対応づけは、相手の索引 (問 → 相手のページ) と自分の索引
 *   (相手のページ → 自分の問) の両方を使う。どちらも「資料を開いたときだけ取る」。
 * @failure: 取得失敗は console に出し、その資料は空の索引にする。
 */

import { useEffect, useState } from "react";
import { fetchSourceIndex, type StudySourceIndexEntry } from "../api/sourceIndex";

const EMPTY: ReadonlyMap<string, StudySourceIndexEntry[]> = new Map();

export function useSourceIndexes(
  familyId: string,
  sourceIds: readonly string[],
): ReadonlyMap<string, StudySourceIndexEntry[]> {
  const [map, setMap] = useState<ReadonlyMap<string, StudySourceIndexEntry[]>>(EMPTY);
  const key = sourceIds.join("\n");

  useEffect(() => {
    const ids = key === "" ? [] : key.split("\n");
    setMap(EMPTY);
    if (ids.length === 0) return;
    const controller = new AbortController();
    Promise.all(
      ids.map((id) =>
        fetchSourceIndex(familyId, id, controller.signal).catch((err: unknown) => {
          if (!controller.signal.aborted) console.error("[study] failed to load source index", err);
          return [] as StudySourceIndexEntry[];
        }),
      ),
    ).then((lists) => {
      if (controller.signal.aborted) return;
      setMap(new Map(ids.map((id, i) => [id, lists[i] ?? []])));
    });
    return () => controller.abort();
  }, [familyId, key]);

  return map;
}
